import { useCallback, useEffect, useState } from 'react';

/**
 * A hook to track the which item visible to the user within the window.
 *
 * @param targets - list of css selector's strings
 * @param elementWidth - element elementWidth size to observer if there is a change on the window size
 * @param detectionOffset - the distance to mark active index
 */
export const useActiveItem = (targets: string[], elementWidth?: number, detectionOffset = 0) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [elementMetadata, setElementMetadata] = useState<Array<null | { top: number; height: number }>>([]);

  useEffect(() => {
    if (elementWidth) {
      // Update the offset if there is a change in elementWidth
      const map = targets.map((link) => {
        const htmlElement = document.querySelector(link) as HTMLElement;
        if (!htmlElement) {
          return null;
        }

        const { offsetTop, offsetHeight } = htmlElement;
        return { top: offsetTop, height: offsetHeight };
      });

      setElementMetadata(map);
    }
  }, [targets, elementWidth]);

  const handleTopOutOfBound = useCallback(
    (top: number) => {
      if (activeIndex !== null) {
        const firstElementMeta = elementMetadata?.[0];
        if (firstElementMeta) {
          const firstElementTop = firstElementMeta.top;
          if (top < firstElementTop + detectionOffset) {
            setActiveIndex(null);
          }
        }

        const lastElement = elementMetadata?.[elementMetadata.length - 1];
        if (lastElement) {
          const lastElementEnd = lastElement.height + lastElement.top;
          if (top > lastElementEnd + detectionOffset) {
            setActiveIndex(null);
          }
        }
      }
    },
    [elementMetadata, activeIndex, detectionOffset],
  );

  const handleTopWithinContainer = useCallback(
    (top: number) => {
      // Avoid clash with `for-of recommendation` since for-of is heavier than normal for loop
      // eslint-disable-next-line unicorn/no-for-loop
      for (let index = 0; index < elementMetadata.length; index++) {
        const metadata = elementMetadata[index];
        if (metadata) {
          const elementPositionStart = metadata.top;
          const elementPositionEnd = metadata.top + metadata.height;
          const isWithinElement =
            top >= elementPositionStart + detectionOffset && top <= elementPositionEnd + detectionOffset;

          if (isWithinElement) {
            const isNewIndex = index !== activeIndex;

            if (isNewIndex) {
              setActiveIndex(index);
            }

            break;
          }
        }
      }
    },
    [elementMetadata, activeIndex, detectionOffset],
  );

  const updateActiveIndex = useCallback(
    (top: number) => {
      handleTopWithinContainer(top);
      handleTopOutOfBound(top);
    },
    [handleTopWithinContainer, handleTopOutOfBound],
  );

  return { activeIndex, updateActiveIndex, elementMetadata };
};
